import { useMemo, useState } from 'react';
import { RISK_PRODUCTS, RISK_CURRENCIES, RISK_QUICK, SL_QUICK } from '@/mocks/risk';
import { formatVN, Label, NumberInput, SelectInput, QuickChips, Stat, Slider, NextStep } from '../ui';

const POS_ACCENT = '#10b981';

const WHEN_CARDS = [
  {
    title: 'Trước khi vào lệnh',
    desc: 'Xác định chính xác số Lot để mỗi lệnh chỉ rủi ro đúng % vốn bạn đã cam kết — không mở lệnh quá tay, không để cảm xúc chi phối quyết định.',
    example: 'Vốn $10.000, rủi ro 2% → tối đa $200 cho mỗi lệnh.',
  },
  {
    title: 'Khống chế thua lỗ',
    desc: 'Khóa chặt mức thua lỗ tối đa dựa trên số dư và khoảng cách cắt lỗ. Dù thị trường đi ngược, bạn biết trước mình có thể mất bao nhiêu.',
    example: 'Stop Loss càng xa, số Lot càng nhỏ — tổn thất vẫn giữ nguyên trong ngưỡng.',
  },
  {
    title: 'So sánh kịch bản',
    desc: 'Thử nhiều mức rủi ro và Stop Loss khác nhau để xem khối lượng thay đổi thế nào, rồi chọn phương án cân bằng giữa lợi nhuận và an toàn.',
    example: 'Kéo rủi ro từ 1% lên 3% và quan sát số Lot thay đổi tức thì.',
  },
];

export default function PositionSizeTab({ onNext }: { onNext: () => void }) {
  const [balance, setBalance] = useState(10000);
  const [risk, setRisk] = useState(2);
  const [stopLoss, setStopLoss] = useState(50);
  const [pair, setPair] = useState('EURUSD');
  const [currency, setCurrency] = useState('USD');

  const product = useMemo(() => RISK_PRODUCTS.find((p) => p.value === pair) ?? RISK_PRODUCTS[0], [pair]);
  const curr = useMemo(() => RISK_CURRENCIES.find((c) => c.value === currency) ?? RISK_CURRENCIES[0], [currency]);

  const riskAmount = (balance * risk) / 100;
  const pipValueAccount = product.pipValue * curr.rate;
  const lotSize = stopLoss > 0 && pipValueAccount > 0 ? riskAmount / (stopLoss * pipValueAccount) : 0;
  const positionValue = lotSize * product.contractSize * product.price;

  const invalid = balance <= 0 || stopLoss <= 0;
  const riskLevel = risk <= 1 ? 'Thấp' : risk <= 2 ? 'Trung bình' : 'Cao';

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Left: inputs */}
        <div className="space-y-6 lg:col-span-3">
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
            <div className="mb-6 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <i className="ri-crosshair-line text-lg" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold leading-tight text-slate-900">Thông tin rủi ro</h3>
                <p className="text-xs text-slate-400">Kết quả cập nhật tức thì khi bạn thay đổi</p>
              </div>
            </div>

            <div className="space-y-5">
              <div>
                <Label>Vốn tài khoản</Label>
                <NumberInput value={balance} onChange={setBalance} suffix={curr.value} min={0} step={100} />
              </div>

              <div>
                <Label hint={`${formatVN(risk, 1, 1)}%`}>Tỷ lệ rủi ro mỗi lệnh</Label>
                <Slider value={risk} onChange={setRisk} min={0.5} max={5} step={0.5} accent={POS_ACCENT} />
                <div className="mt-2.5">
                  <QuickChips options={RISK_QUICK} value={risk} onChange={setRisk} format={(v) => `${formatVN(v, 1, 1)}%`} />
                </div>
              </div>

              <div>
                <Label hint={`${formatVN(stopLoss, 0, 0)} pips`}>Khoảng cách cắt lỗ (Stop Loss)</Label>
                <NumberInput value={stopLoss} onChange={setStopLoss} suffix="pips" min={0} step={5} />
                <div className="mt-2.5">
                  <QuickChips options={SL_QUICK} value={stopLoss} onChange={setStopLoss} />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <Label>Sản phẩm giao dịch</Label>
                  <SelectInput value={pair} onChange={setPair} options={RISK_PRODUCTS.map((p) => ({ value: p.value, label: p.label }))} />
                </div>
                <div>
                  <Label>Tiền tệ tài khoản</Label>
                  <SelectInput value={currency} onChange={setCurrency} options={RISK_CURRENCIES.map((c) => ({ value: c.value, label: c.label }))} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right: big result */}
        <div className="lg:col-span-2">
          <div
            className="relative flex h-full flex-col overflow-hidden rounded-3xl border p-6 md:p-7"
            style={{
              borderColor: `${POS_ACCENT}38`,
              background: `linear-gradient(160deg, ${POS_ACCENT}12 0%, #ffffff 55%)`,
            }}
          >
            <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full blur-3xl" style={{ background: `${POS_ACCENT}24` }} />

            <div className="relative">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-500">Khối lượng đề xuất</p>
                <span className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold" style={{ backgroundColor: `${POS_ACCENT}1f`, color: POS_ACCENT }}>
                  <i className="ri-shield-check-line" />
                  {riskLevel} rủi ro
                </span>
              </div>

              <div className="mt-6 text-center">
                {invalid ? (
                  <p className="text-sm text-slate-400">Nhập số dư và Stop Loss hợp lệ để tính khối lượng.</p>
                ) : (
                  <>
                    <p className="font-mono text-5xl font-bold leading-none tabular-nums md:text-6xl" style={{ color: POS_ACCENT, textShadow: `0 0 34px ${POS_ACCENT}55` }}>
                      {formatVN(lotSize, 2, 2)}
                    </p>
                    <p className="mt-3 text-sm text-slate-500">Lot · {product.label}</p>
                  </>
                )}
              </div>

              <div className="mt-7 grid grid-cols-2 gap-2.5">
                <Stat label="Số tiền rủi ro" value={`${formatVN(riskAmount, 0, 0)} ${curr.value}`} accent={POS_ACCENT} />
                <Stat label="Giá trị mỗi Pip" value={`${formatVN(pipValueAccount, 2, 2)} ${curr.value}`} />
                <Stat label="Giá trị vị thế" value={`${formatVN(positionValue, 0, 0)} ${curr.value}`} />
                <Stat label="Tiền tệ" value={curr.value} />
              </div>

              <div className="mt-6 rounded-2xl border border-slate-200/80 bg-white/70 p-4">
                <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400">Cách tính</p>
                <p className="mt-2 font-mono text-xs leading-relaxed text-slate-600">
                  Lot = ({formatVN(balance, 0, 0)} × {formatVN(risk, 1, 1)}%) ÷ ({formatVN(stopLoss, 0, 0)} × {formatVN(pipValueAccount, 2, 2)})
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* When to use */}
      <div className="rounded-3xl border border-slate-200/80 bg-white p-6 md:p-8">
        <h3 className="font-display text-lg font-bold text-slate-900">Ứng dụng chính</h3>
        <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
          {WHEN_CARDS.map((c) => (
            <div key={c.title} className="rounded-2xl border border-slate-200/80 bg-slate-50/60 p-5">
              <span className="block h-1 w-8 rounded-full bg-emerald-500" />
              <p className="mt-4 text-sm font-bold text-slate-800">{c.title}</p>
              <p className="mt-2 text-xs leading-relaxed text-slate-500">{c.desc}</p>
              <div className="mt-4 rounded-xl border border-slate-200/70 bg-white/80 p-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-600">Ví dụ</p>
                <p className="mt-1 font-mono text-[11px] leading-relaxed text-slate-600">{c.example}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <NextStep
        title="Bạn đã biết khối lượng phù hợp với rủi ro."
        desc="Bước tiếp theo: mô phỏng mức sụt giảm tối đa của tài khoản."
        cta="Máy tính Drawdown"
        onClick={onNext}
      />
    </div>
  );
}